/**
 * Display helpers for psalm text. Pure and deterministic — SSR and the browser must
 * produce byte-identical markup or hydration mismatches.
 */
import type { ReactNode } from 'react'

/**
 * `LORD` and `GOD` in the psalter are not shouting. They are the BCP's small
 * capitals, standing where the Hebrew has the Tetragrammaton (and, for `GOD`, where
 * a second divine name follows it). Printing them as plain capitals is legible but
 * wrong-looking to anyone who knows the book; printing them in small caps is what
 * makes the page read as a psalter rather than as scraped text.
 *
 * Only these two words qualify. A general "capitalize runs of caps" rule would also
 * catch nothing else in this corpus today, but it would be a rule about typography
 * rather than about the divine name, and it would quietly restyle any future text
 * that happened to contain an acronym.
 */
const DIVINE_NAME = /\b(LORD|GOD)\b/g

/**
 * ⚠️ THE OBVIOUS IMPLEMENTATION IS WRONG, and it shipped once before being caught.
 *
 * `font-variant-caps: small-caps` does nothing to text that is ALREADY uppercase —
 * the feature maps lowercase letters onto small-cap glyphs, and `LORD` has none.
 * The usual fix is to add `text-transform: lowercase` so the variant has something
 * to work on. That renders correctly and corrupts the text: `text-transform`
 * changes the glyphs the browser lays down, so the PDF's extracted text came out
 * as **"The lord is my shepherd"**, and a reader copying a verse off the page, a
 * screen reader announcing it, and a crawler indexing it would all get the divine
 * name lowercased. On a psalter that is not a styling nit.
 *
 * So this splits the word instead: the initial at full size, the remainder at
 * 0.82em. Both halves are real characters, so `LORD` survives copy, paste, search
 * and speech intact — and the visual result is what small caps are for.
 */
export function psalmText(s: string): ReactNode[] {
  const out: ReactNode[] = []
  let last = 0
  let m: RegExpExecArray | null
  // `lastIndex` is reset per call because the regex is module-scoped with /g and
  // would otherwise carry state between verses — a classic source of every-other-
  // match bugs, and one that would differ between SSR and client render order.
  DIVINE_NAME.lastIndex = 0
  while ((m = DIVINE_NAME.exec(s)) !== null) {
    if (m.index > last) out.push(s.slice(last, m.index))
    const word = m[0]
    out.push(
      <span key={`${m.index}-${word}`} className="divine-name">
        {word.slice(0, 1)}
        <span className="divine-name-rest">{word.slice(1)}</span>
      </span>
    )
    last = m.index + word.length
  }
  if (last < s.length) out.push(s.slice(last))
  return out
}

/**
 * The first half of a verse ends at the asterisk. Rendering the asterisk is not
 * decoration — it is the one piece of performance instruction the BCP psalter
 * actually carries, telling two sides of a choir or congregation where to hand over.
 * Omitting it would make the text look pointed when it is not.
 */
export const ASTERISK = ' *'
